import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useEventCallback, useEventListener } from '.';

declare global {
  interface WindowEventMap {
    'local-storage': CustomEvent;
  }
}

type SetValue<T> = Dispatch<SetStateAction<T>>;

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  useLocalMem?: boolean, // Use this flag as a "disable" for local storage
): [T, SetValue<T>] {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback((): T => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined' || useLocalMem) {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (parseJSON(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key, useLocalMem]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: SetValue<T> = useEventCallback(value => {
    if (useLocalMem) {
      setStoredValue(value);
      return;
    }

    // Prevent build error "window is undefined" but keeps working
    if (typeof window === 'undefined') {
      console.warn(
        `Tried setting localStorage key “${key}” even though environment is not a client`,
      );
    }

    // Allow value to be a function so we have the same API as useState
    const newValue = value instanceof Function ? value(storedValue) : value;

    // Save state first. setItem throws whenever the browser refuses to store
    // anything -- site data blocked, quota full, a partitioned iframe -- and
    // updating state inside the same try meant the value the user just chose
    // was discarded along with the failed write. The control then snapped
    // back and nothing downstream saw the change, which is worse than losing
    // it at the end of the session.
    setStoredValue(newValue);

    let serialized: string | null = null;

    try {
      serialized = JSON.stringify(newValue);

      // Save to local storage
      window.localStorage.setItem(key, serialized);
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }

    // We dispatch an event so every useLocalStorage hook is notified. It
    // carries the key, the way useSessionStorage already does: a keyless
    // event makes every instance on every OTHER key re-read from disk, which
    // reverts whatever a refused write left in memory. It is dispatched even
    // when that write failed, so hooks on this key adopt the value from the
    // event rather than each keeping a different answer.
    window.dispatchEvent(
      new StorageEvent('local-storage', { key, newValue: serialized }),
    );
  });

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      if (useLocalMem) return;

      // A null key is a whole-store clear, which every instance must follow.
      const changedKey = (event as StorageEvent)?.key;
      if (changedKey && changedKey !== key) return;

      // Prefer the value the event carries. After a refused write the disk
      // still holds the previous value, so re-reading it would revert what
      // the writer is already showing the user.
      const dispatched = (event as StorageEvent)?.newValue;
      setStoredValue(
        dispatched == null ? readValue() : (parseJSON(dispatched) as T),
      );
    },
    [key, readValue, useLocalMem],
  );

  // this only works for other documents, not the current one
  useEventListener('storage', handleStorageChange);

  // this is a custom event, triggered in writeValueToLocalStorage
  // See: useLocalStorage()
  useEventListener('local-storage', handleStorageChange);

  return [storedValue, setValue];
}

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    console.error('parsing error on', { value });
    return undefined;
  }
}
